import { Directive, Input, ElementRef, HostListener, OnInit, Renderer2 } from "@angular/core";

const MIN_POS: any = { x: 0, y: 50, b: 30 };
// b min bottom position

@Directive({
  selector: "[ng2-draggable]",
  standalone: false,
})
export class Draggable implements OnInit {
  private topStart: number;
  private leftStart: number;
  private _allowDrag: boolean = true;
  private mousemove: () => void;
  private touchmove: () => void;
  private _handle: HTMLElement;

  constructor(
    public element: ElementRef,
    private renderer: Renderer2,
  ) {}

  ngOnInit() {
    // css changes
    if (this._allowDrag) {
      this.element.nativeElement.className += " cursor-draggable";
    }
  }

  @HostListener("document:mousedown", ["$event"])
  onMouseDown(event: MouseEvent) {
    if (!this.isTargetHandleOrChild(event)) {
      // logger.out.info(`${this.constructor.name}.onMouseDown ignoring out of handle interaction`)
      return true;
    }

    if (event.button === 2) return; // prevents right click drag, remove his if you don't want it

    if (this.isInMouseBoundary(event)) {
      if (this.mousemove) {
        this.mousemove();
        this.mousemove = null;
      }
      this.mousemove = this.renderer.listen("document", "mousemove", (event) =>
        this.onMouseMove(event),
      );
      this.topStart = event.clientY - this.element.nativeElement.style.top.replace("px", "");
      this.leftStart = event.clientX - this.element.nativeElement.style.left.replace("px", "");
    }
  }

  @HostListener("document:mouseup", ["$event"])
  onMouseUp(event: MouseEvent) {
    if (this.mousemove) {
      this.mousemove();
      this.mousemove = null;
    }
  }

  onMouseMove(event: MouseEvent) {
    if (this.mousemove && this._allowDrag && this.isInMouseBoundary(event)) {
      this.element.nativeElement.style.top =
        Math.max(
          MIN_POS.y,
          Math.min(window.innerHeight - MIN_POS.b, event.clientY - this.topStart),
        ) + "px";
      this.element.nativeElement.style.left =
        Math.max(
          MIN_POS.x,
          Math.min(
            window.innerWidth - this.element.nativeElement.offsetWidth,
            event.clientX - this.leftStart,
          ),
        ) + "px";
    }
  }

  @HostListener("document:mouseleave", ["$event"])
  onMouseLeave(event: MouseEvent) {
    if (this.mousemove) {
      this.mousemove();
      this.mousemove = null;
    }
  }

  @HostListener("touchstart", ["$event"])
  onTouchStart(event: TouchEvent) {
    if (this.isInTouchBoundary(event)) {
      if (this.touchmove) {
        this.touchmove();
        this.touchmove = null;
      }
      this.touchmove = this.renderer.listen("document", "touchmove", (event) =>
        this.onTouchMove(event),
      );
      this.topStart =
        event.changedTouches[0].clientY - this.element.nativeElement.style.top.replace("px", "");
      this.leftStart =
        event.changedTouches[0].clientX - this.element.nativeElement.style.left.replace("px", "");
      event.stopPropagation();
    }
  }

  @HostListener("document:touchend", ["$event"])
  onTouchEnd($event) {
    if (this.touchmove) {
      this.touchmove();
      this.touchmove = null;
    }
  }

  onTouchMove(event: TouchEvent) {
    if (this.touchmove && this._allowDrag && this.isInTouchBoundary(event)) {
      this.element.nativeElement.style.top =
        Math.max(
          MIN_POS.y,
          Math.min(window.innerHeight - MIN_POS.b, event.changedTouches[0].clientY - this.topStart),
        ) + "px";
      this.element.nativeElement.style.left =
        Math.max(
          MIN_POS.x,
          Math.min(
            window.innerWidth - this.element.nativeElement.offsetWidth,
            event.changedTouches[0].clientX - this.leftStart,
          ),
        ) + "px";
      event.stopPropagation();
    }
  }

  @Input("ng2-draggable")
  set allowDrag(value: boolean) {
    this._allowDrag = value;
    if (this._allowDrag) this.element.nativeElement.className += " cursor-draggable";
    else
      this.element.nativeElement.className = this.element.nativeElement.className.replace(
        " cursor-draggable",
        "",
      );
  }

  @Input()
  set ng2DraggableHandle(handle: HTMLElement) {
    this._handle = handle;
  }

  protected isInMouseBoundary(mouseEvent: MouseEvent): boolean {
    return this.isInBoundary(mouseEvent.clientX, mouseEvent.clientY);
  }

  protected isInTouchBoundary(touchEvent: TouchEvent): boolean {
    return this.isInBoundary(
      touchEvent.changedTouches[0].clientX,
      touchEvent.changedTouches[0].clientY,
    );
  }

  protected isInBoundary(x: number, y: number) {
    return x > MIN_POS.x && y > MIN_POS.y && x < window.innerWidth && y < window.innerHeight;
  }

  protected isTargetHandleOrChild(event: MouseEvent): boolean {
    if (this._handle === undefined) {
      return false;
    }

    let node = event.target;
    let maxdepth = 16;
    let depth = 0;

    while (depth < maxdepth) {
      if (!node) {
        return false;
      } else if (node === this._handle) {
        return true;
      }

      node = node["parentNode"];
      depth++;
    }

    return false;
  }
}
